import { useCallback } from "react";
import { useCollapsibleViewContext } from "@/features/collapsible-view";
import { useDataModelContext } from "@/entities/data-model";
import { getPersistableNameExactness, useMetadataQuery } from "@/entities/form-metadata";
import { useSaveUserPreferencesMutation, useUserPreferencesQuery } from "@/entities/user-preferences";
import { preserveFormStateInCache } from "../form-history";
import type { FormValues } from "../form/types";
import { useFormControls } from "./useFormControls";

interface AdditionalData {
    matchAllTermsExactly: boolean;
    isSkipAssistPlacesModalPermanently?: boolean;
}

interface UseOnSubmitArgs {
    isEditForm: boolean;
}

export const useOnSubmit = ({ isEditForm }: UseOnSubmitArgs) => {
    const dataModel = useDataModelContext();
    const { handleSubmit } = useFormControls(dataModel, isEditForm);
    const { mutate: savePreferences } = useSaveUserPreferencesMutation();
    const { data: userPreference } = useUserPreferencesQuery();
    const { isCollapsed } = useCollapsibleViewContext();
    const { data: formMetadata } = useMetadataQuery();

    const onSubmit = useCallback(
        async (formValues: FormValues, additionalData: Partial<AdditionalData> = {}) => {
            preserveFormStateInCache(formValues);
            savePreferences({
                // The domain layer decides which name exactness values are safe to persist — a
                // collection-locked field's forced value is excluded so it can't leak onto other
                // collections. Submit stays agnostic of that rule. See TT-3965.
                ...getPersistableNameExactness(formValues.name, formMetadata),
                types: formValues.types,
                showMoreOptions: !isCollapsed,
                showUnviewedRecordsOnly: formValues.showUnviewedRecordsOnly,
                collectionFocus: formValues.priority || "default",
                ...additionalData,
            });
            await handleSubmit({
                formValues,
                userPreference,
                matchAllTermsExactly: additionalData.matchAllTermsExactly ?? false,
            });
        },
        [handleSubmit, savePreferences, userPreference, isCollapsed, formMetadata],
    );

    return onSubmit;
};
